import { enSousBulles, indexerSequences, sequencesDeLaCase } from '../components/TableauProgressions.tsx';
import type { ColonneSousDomaine, SequenceCellule } from '../components/TableauProgressions.tsx';

/**
 * Les modèles d'impression de la v1, à l'identique.
 *
 * ── Pourquoi des modèles, et non la copie de l'écran ────────────────────────
 * La v1 imprimait chaque module dans un document fait pour le papier : Arial,
 * filets gris clair, en-têtes ardoise en blanc, couleur de la matière en
 * bandeau. La v2 copiait ses écrans, avec leurs cartes, leurs arrondis et leur
 * police — un autre document, que les enseignants ne reconnaissaient pas. Ces
 * modèles reprennent ceux de la v1 (`public/js/app.js`, `heures.js`), à partir
 * des données des écrans : le module Impressions s'en sert aussi, pour que le
 * dossier et l'écran impriment la même chose.
 *
 * La fiche de préparation n'est PAS ici : elle garde son propre modèle.
 *
 * Fonctions PURES : données en entrée, HTML échappé en sortie.
 */

/** Ce que les modèles rendent : un titre, un corps, leur style, leur orientation. */
export interface DocumentV1 {
  titre: string;
  /** Le contenu, SANS le titre : l'appelant l'ajoute (ou le dossier le sien). */
  html: string;
  style: string;
  orientation: 'portrait' | 'paysage';
}

/**
 * `PRINT_BASE_CSS` de la v1, borné à `.v1` : dans le dossier, il côtoie la
 * fiche de préparation, qui garde son propre style.
 */
export const BASE_V1 = `
  .v1, .v1 * { box-sizing: border-box; font-family: Arial, 'Liberation Sans', Helvetica, sans-serif; }
  .v1 { font-size: 9pt; color: #1a1a1a; background: white; }
  .v1 * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .v1 h1 { font-size: 13pt; font-weight: 700; margin: 0 0 5mm; color: #1a1a1a;
           border-bottom: 2px solid #1a1a1a; padding-bottom: 3mm; }
  .v1 h2 { font-size: 10pt; font-weight: 700; margin: 6mm 0 2mm; padding-bottom: 1mm;
           border-bottom: 1px solid #666; }
  .v1 table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .v1 th, .v1 td { border: 1px solid #bbb; padding: 4px 6px; vertical-align: top; font-size: 8.5pt;
                   overflow-wrap: anywhere; }
  .v1 th { font-weight: 700; text-align: center; }
  .v1 td { background: white; }
  .v1 thead { display: table-header-group; }
  .v1 .v1-saut { break-before: page; }
`;

const ARDOISE = '#2d3748';

export function echapper(t: string): string {
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const lignesTexte = (t: string) => echapper(t).replace(/\n/g, '<br>');

/** Une couleur saisie par l'enseignant, ou un repli : jamais rien d'autre dans un style. */
const teinte = (c: string | null | undefined, repli = '#667eea') =>
  (c && /^#[0-9a-f]{6}$/i.test(c) ? c : repli);

/** « 08:30 » → « 8h30 » : la forme de la v1. */
export const heureV1 = (h: string | null | undefined) =>
  (h ? `${Number(h.slice(0, 2))}h${h.slice(3, 5)}` : '');

/** Les morceaux du titre, joints comme dans la v1 : « Mathématiques — Période 2 — 2026-2027 ». */
export const titreV1 = (...morceaux: Array<string | null | undefined>) =>
  morceaux.filter((m) => m && m.trim()).join(' — ');

/** Le document complet d'un module : son titre en tête, comme dans la v1. */
export function corpsV1(doc: DocumentV1): string {
  return `<div class="v1"><h1>${echapper(doc.titre)}</h1>${doc.html}</div>`;
}

// ── Cahier journal ───────────────────────────────────────────────────────────

export interface LigneJournalV1 {
  debut: string | null;
  fin: string | null;
  domaine: string;
  sousDomaine: string;
  couleur: string;
  objectifs: Array<{ texte: string; fait?: boolean }>;
  deroulement: string;
  materiel: string;
}

/**
 * Une journée du cahier journal — `Journal.imprimer` de la v1.
 *
 * A4 portrait ; l'essentiel de la largeur au déroulement ; en-tête ardoise ;
 * chaque ligne porte la couleur de sa matière en bande à gauche, chaque
 * objectif la sienne.
 */
export function tableJournalV1(lignes: LigneJournalV1[]): string {
  const corps = lignes.map((l) => {
    const couleur = teinte(l.couleur, '#94a3b8');
    return `<tr style="border-left:4px solid ${couleur}">
      <td><div class="jv1-heure">${heureV1(l.debut)}</div><div class="jv1-heure">${heureV1(l.fin)}</div></td>
      <td><div class="jv1-domaine">${echapper(l.domaine)}</div>${l.sousDomaine ? `<div class="jv1-texte">${echapper(l.sousDomaine)}</div>` : ''}</td>
      <td>${l.objectifs.map((o) => `<div class="jv1-obj" style="border-left-color:${couleur};background:${o.fait ? '#f0fdf4' : '#fafafa'}">${echapper(o.texte)}</div>`).join('')}</td>
      <td><div class="jv1-texte">${lignesTexte(l.deroulement)}</div></td>
      <td><div class="jv1-texte">${lignesTexte(l.materiel)}</div></td>
    </tr>`;
  }).join('');
  return `<table class="jv1">
    <colgroup><col style="width:7%"><col style="width:14%"><col style="width:24%"><col style="width:45%"><col style="width:10%"></colgroup>
    <thead><tr><th>Horaire</th><th>Domaine / Sous-domaine</th><th>Objectifs</th><th>Déroulement de l’activité</th><th>Matériel</th></tr></thead>
    <tbody>${corps}</tbody>
  </table>`;
}

export const STYLE_JOURNAL_V1 = `
  .jv1 thead th { background: ${ARDOISE}; color: white; padding: 5px 4px; font-size: 8pt;
                  overflow-wrap: normal; word-break: normal; hyphens: manual; }
  .jv1 td { padding: 5px 7px; word-break: break-word; }
  .jv1 tr { break-inside: avoid; }
  .jv1-heure { font-size: 8.5pt; color: #334155; }
  .jv1-domaine { font-size: 8.5pt; font-weight: 600; color: #1e293b; }
  .jv1-texte { font-size: 8.5pt; color: #334155; white-space: pre-wrap; word-break: break-word; }
  .jv1-obj { border-left: 3px solid #667eea; padding: 4px 6px; margin-bottom: 4px; border-radius: 4px;
             font-size: 8pt; line-height: 1.4; }
  .jv1-jour h1 { text-transform: none; }
  .jv1-titre::first-letter { text-transform: uppercase; }
`;

export function journalV1(titre: string, lignes: LigneJournalV1[]): DocumentV1 {
  return {
    titre,
    html: tableJournalV1(lignes),
    style: STYLE_JOURNAL_V1,
    orientation: 'portrait',
  };
}

// ── Progressions ─────────────────────────────────────────────────────────────

export interface SemaineV1 {
  lundi: string;
  vendredi: string;
  rang: number;
  vacances?: boolean;
}

const dateCourte = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', {
  day: 'numeric', month: 'short', timeZone: 'UTC',
});

/**
 * Une matière sur une période — `Progression._corpsImpression` de la v1.
 *
 * Au-delà de six sous-domaines, le tableau est découpé en blocs de cinq,
 * chacun reprenant la colonne « Semaine » et commençant sur une page neuve.
 */
export function progressionsV1(
  titre: string,
  couleur: string,
  semaines: SemaineV1[],
  colonnes: ColonneSousDomaine[],
  sequences: SequenceCellule[],
): DocumentV1 {
  const c = teinte(couleur);
  const index = indexerSequences(sequences);
  const blocs: ColonneSousDomaine[][] = [];
  if (colonnes.length <= 6) blocs.push(colonnes);
  else for (let i = 0; i < colonnes.length; i += 5) blocs.push(colonnes.slice(i, i + 5));

  const table = (cols: ColonneSousDomaine[]) => {
    const part = (91 / Math.max(1, cols.length)).toFixed(3);
    return `<table class="pv1">
      <colgroup><col style="width:9%">${cols.map(() => `<col style="width:${part}%">`).join('')}</colgroup>
      <thead><tr><th style="background:${c};color:white">Semaine</th>
        ${cols.map((col) => `<th class="pv1-sd" style="background:${c}dd;color:white">${echapper(col.nom)}</th>`).join('')}</tr></thead>
      <tbody>${semaines.map((s) => `<tr${s.vacances ? ' class="pv1-vac"' : ''}>
        <td class="pv1-semaine"><div class="pv1-num">S${s.rang}</div><div class="pv1-dates">${dateCourte(s.lundi)}</div><div class="pv1-dates">${dateCourte(s.vendredi)}</div></td>
        ${cols.map((col) => `<td>${sequencesDeLaCase(index, col, s.lundi).map((q) => `
          <div class="pv1-entree" style="border-left-color:${c};background:${c}18${q.done ? ';opacity:.5' : ''}">
            ${q.sequence ? `<div class="pv1-seq">${echapper(q.sequence)}</div>` : ''}
            ${enSousBulles(q.competencies).map((b) => `<div class="pv1-detail">${echapper(b)}</div>`).join('')}
          </div>`).join('')}</td>`).join('')}
      </tr>`).join('')}</tbody>
    </table>`;
  };

  const html = blocs.map((cols, i) => `<div class="pv1-bloc${i > 0 ? ' v1-saut' : ''}">
    ${blocs.length > 1 ? `<h2 class="pv1-h2">${echapper(titre)} — colonnes ${i + 1}/${blocs.length}</h2>` : ''}
    ${table(cols)}</div>`).join('');
  return { titre, html, style: STYLE_PROGRESSIONS_V1, orientation: 'paysage' };
}

export const STYLE_PROGRESSIONS_V1 = `
  .pv1 thead th { text-align: center; padding: 6px 4px; }
  .pv1 .pv1-sd { padding: 5px; font-size: 8pt; }
  .pv1 tbody td { padding: 4px; word-break: break-word; }
  .pv1 tbody tr { break-inside: avoid; }
  .pv1-semaine { background: #f0f0f0 !important; text-align: center; font-weight: 700; }
  .pv1-num { font-size: 10pt; font-weight: 800; color: #1a1a1a; }
  .pv1-dates { font-size: 7pt; color: #555; font-weight: 400; }
  .pv1-vac td { background: #f8f8f8; }
  .pv1-entree { border-left: 3px solid #667eea; padding: 3px 5px; margin-bottom: 3px; border-radius: 2px; }
  .pv1-seq { font-weight: 700; font-size: 8.5pt; color: #1a1a1a; margin-bottom: 2px; }
  .pv1-detail { font-size: 7.5pt; color: #444; }
  .pv1-h2 { font-size: 9pt !important; border: none !important; margin: 0 0 3mm !important; color: #333; }
`;

// ── Programmation ────────────────────────────────────────────────────────────

export interface PeriodeV1 {
  id: string;
  nom: string;
  semaines: number;
  debut: string;
  fin: string;
}

export interface LigneProgrammationV1 {
  nom: string;
  /** Blocs de la ligne, par période. */
  blocs: (periodeId: string) => string[];
}

/** Une matière sur l'année — `Programmation._corpsImpression` de la v1. */
export function programmationV1(
  titre: string,
  couleur: string,
  periodes: PeriodeV1[],
  lignes: LigneProgrammationV1[],
): DocumentV1 {
  const c = teinte(couleur);
  const html = `<table class="gv1">
    <colgroup><col style="width:130px">${periodes.map(() => '<col>').join('')}</colgroup>
    <thead><tr><th style="background:${ARDOISE};color:white">Sous-domaine</th>
      ${periodes.map((p) => `<th style="background:${c};color:white">
        <div class="gv1-per">${echapper(p.nom)}</div>
        <div class="gv1-petit">${p.semaines} semaine${p.semaines > 1 ? 's' : ''}</div>
        <div class="gv1-petit">${dateCourte(p.debut)} – ${dateCourte(p.fin)}</div></th>`).join('')}</tr></thead>
    <tbody>${lignes.map((l) => `<tr>
      <td class="gv1-sd" style="border-left:4px solid ${c}">${echapper(l.nom)}</td>
      ${periodes.map((p) => `<td>${l.blocs(p.id).map((b) => `<div class="gv1-bloc" style="border-left-color:${c};background:${c}15">${echapper(b)}</div>`).join('')}</td>`).join('')}
    </tr>`).join('')}</tbody>
  </table>`;
  return { titre, html, style: STYLE_PROGRAMMATION_V1, orientation: 'paysage' };
}

export const STYLE_PROGRAMMATION_V1 = `
  .gv1 thead th { padding: 5px 4px; }
  .gv1 tbody td { padding: 4px; word-break: break-word; }
  .gv1 tbody tr:nth-child(even) td { background: #fafafa; }
  .gv1 .gv1-sd { background: #f0f0f0 !important; font-weight: 700; font-size: 8.5pt; }
  .gv1-per { font-size: 8.5pt; font-weight: 700; }
  .gv1-petit { font-size: 7pt; opacity: .9; font-weight: 400; }
  .gv1-bloc { border-left: 3px solid #667eea; padding: 3px 5px; margin-bottom: 3px; border-radius: 2px;
              font-size: 8.5pt; color: #1a1a1a; white-space: pre-wrap; }
`;

// ── Évaluation ───────────────────────────────────────────────────────────────

/** Les pastilles de la v1 : fond plein, texte blanc. */
const PASTILLES: Record<string, { fond: string; texte: string; libelle: string }> = {
  '': { fond: '#e2e8f0', texte: '#64748b', libelle: '–' },
  bleu: { fond: '#3b82f6', texte: 'white', libelle: 'NA' },
  orange: { fond: '#f97316', texte: 'white', libelle: 'PA' },
  jaune: { fond: '#eab308', texte: 'white', libelle: 'A' },
  vert: { fond: '#22c55e', texte: 'white', libelle: 'D' },
};

const pastille = (niveau: string | null | undefined) => {
  const p = PASTILLES[niveau ?? ''] ?? PASTILLES['']!;
  return `<span class="ev1-badge" style="background:${p.fond};color:${p.texte}">${p.libelle}</span>`;
};

export interface GrilleV1 {
  students: Array<{ id: string; lastName: string; firstName: string }>;
  domains: Array<{ name: string; assessments: Array<{ id: string; number: number; label: string }> }>;
  marks: Array<{ assessmentId: string; studentId: string; level: string }>;
  averages: Array<{ domainName: string; studentId: string; level: string | null }>;
}

/** Les grilles d'une matière — `Evaluation.imprimer` de la v1, un tableau par domaine. */
export function evaluationV1(titre: string, grille: GrilleV1): DocumentV1 {
  const note = new Map(grille.marks.map((m) => [`${m.assessmentId}:${m.studentId}`, m.level]));
  const html = grille.domains.filter((d) => d.assessments.length > 0).map((d) => {
    const moyenne = new Map(grille.averages.filter((a) => a.domainName === d.name).map((a) => [a.studentId, a.level]));
    return `<h2>${echapper(d.name)}</h2>
      <table class="ev1">
        <colgroup><col style="width:22%">${d.assessments.map(() => '<col>').join('')}<col style="width:11%"></colgroup>
        <thead><tr><th class="ev1-eleve">Élève</th>
          ${d.assessments.map((a) => `<th><div class="ev1-num">Contrôle ${a.number}</div><div class="ev1-libelle">${echapper(a.label)}</div></th>`).join('')}
          <th>Moyenne</th></tr></thead>
        <tbody>${grille.students.map((e) => `<tr>
          <td class="ev1-nom">${echapper(`${e.lastName} ${e.firstName}`.trim())}</td>
          ${d.assessments.map((a) => `<td class="ev1-note">${pastille(note.get(`${a.id}:${e.id}`))}</td>`).join('')}
          <td class="ev1-note">${pastille(moyenne.get(e.id))}</td>
        </tr>`).join('')}</tbody>
      </table>`;
  }).join('');
  const legende = `<div class="ev1-legende">${['bleu', 'orange', 'jaune', 'vert']
    .map((n) => `${pastille(n)} ${{ bleu: 'Non atteint', orange: 'Partiellement atteint', jaune: 'Atteint', vert: 'Dépassé' }[n]}`).join(' &nbsp; ')}</div>`;
  return {
    titre,
    html: html ? legende + html : '<p>Aucun contrôle saisi.</p>',
    style: STYLE_EVALUATION_V1,
    orientation: 'paysage',
  };
}

export const STYLE_EVALUATION_V1 = `
  .ev1 th, .ev1 td { border: 1px solid #ccc; padding: 4px 6px; font-size: 8.5pt; vertical-align: middle; }
  .ev1 thead th { background: ${ARDOISE}; color: white; font-size: 8pt; padding: 6px 8px; }
  .ev1 .ev1-eleve { text-align: left; }
  .ev1 tbody tr:nth-child(even) td { background: #fafafa; }
  .ev1 tbody tr { break-inside: avoid; }
  .ev1-nom { padding: 5px 8px; font-size: 9pt; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ev1-note { text-align: center; }
  .ev1-num { font-weight: 800; }
  .ev1-libelle { font-weight: 400; font-size: 7.5pt; }
  .ev1-badge { display: inline-block; padding: 2px 8px; border-radius: 8px; font-size: 8pt; font-weight: 800; }
  .ev1-legende { font-size: 8pt; margin-bottom: 3mm; }
  .v1 .ev1 + h2 { margin-top: 6mm; }
`;

/**
 * Les statistiques restent la copie de l'écran — un tableau par matière —,
 * habillée comme `Evaluation._imprimerStats` de la v1 : Arial, en-têtes
 * ardoise, une ligne sur deux grisée.
 */
export const STYLE_STATS_V1 = `
  body, body * { font-family: Arial, 'Liberation Sans', Helvetica, sans-serif !important; }
  h1.titre-impression { font-size: 13pt; border-bottom: 2px solid #1a1a1a; }
  [data-impression="evaluation"] table { border-collapse: collapse; margin-bottom: 4mm; break-inside: avoid; }
  [data-impression="evaluation"] th { background: ${ARDOISE} !important; color: white !important;
    padding: 5px 7px; font-size: 8pt; font-weight: 700; border: 1px solid #ccc; }
  [data-impression="evaluation"] td { border: 1px solid #ccc; padding: 5px 8px; font-size: 9pt; }
  [data-impression="evaluation"] tbody tr:nth-child(even) td { background: #fafafa; }
`;

// ── 108 heures ───────────────────────────────────────────────────────────────

export interface PosteV1 { nom: string; fait: number; cible: number }
export interface ActiviteV1 { date: string; categorie: string; minutes: number; intitule: string }

/** `Heures.imprimer` de la v1 : récapitulatif des objectifs, puis tous les créneaux. */
export function heuresV1(
  titre: string,
  postes: PosteV1[],
  total: { fait: number; cible: number },
  activites: ActiviteV1[],
  duree: (minutes: number) => string,
): DocumentV1 {
  const reste = (fait: number, cible: number) => (fait >= cible ? 'atteint' : duree(Math.max(0, cible - fait)));
  const date = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
  const html = `<h2>Récapitulatif</h2>
    <table class="hv1">
      <colgroup><col style="width:40%"><col style="width:20%"><col style="width:20%"><col style="width:20%"></colgroup>
      <thead><tr><th style="text-align:left">Objectif</th><th>Effectué</th><th>Cible</th><th>Restant</th></tr></thead>
      <tbody>${postes.map((p) => `<tr><td>${echapper(p.nom)}</td><td class="hv1-d">${duree(p.fait)}</td>`
        + `<td class="hv1-d">${duree(p.cible)}</td><td class="hv1-d">${reste(p.fait, p.cible)}</td></tr>`).join('')}
        <tr style="font-weight:700"><td>Total</td><td class="hv1-d">${duree(total.fait)}</td>`
        + `<td class="hv1-d">${duree(total.cible)}</td><td class="hv1-d">${reste(total.fait, total.cible)}</td></tr>
      </tbody>
    </table>
    <h2>Détail des créneaux (${activites.length})</h2>
    <table class="hv1">
      <colgroup><col style="width:22%"><col style="width:26%"><col style="width:14%"><col style="width:38%"></colgroup>
      <thead><tr><th style="text-align:left">Date</th><th style="text-align:left">Catégorie</th><th>Durée</th><th style="text-align:left">Intitulé</th></tr></thead>
      <tbody>${activites.map((a) => `<tr><td>${date(a.date)}</td><td>${echapper(a.categorie)}</td>`
        + `<td class="hv1-d">${duree(a.minutes)}</td><td>${echapper(a.intitule)}</td></tr>`).join('')}</tbody>
    </table>`;
  return { titre, html, style: STYLE_HEURES_V1, orientation: 'portrait' };
}

export const STYLE_HEURES_V1 = `
  .hv1 { margin-bottom: 4mm; }
  .hv1 thead th { background: ${ARDOISE}; color: white; }
  .hv1 tbody tr:nth-child(even) td { background: #fafafa; }
  .hv1 .hv1-d { text-align: right; }
`;
